import React, { useState, useEffect } from 'react';
import ComponentRenderer from "../utils/ComponentRenderer";
import cart from "../services/cart";
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {useAppId} from "../AppIdContext";
import {toast, ToastContainer} from "react-toastify";

const ItemDetails = ({ config }) => {
    const appId = useAppId();
    const { state } = useLocation();
    const { item } = state || {};
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [selectedVariation, setSelectedVariation] = useState(null);
    const [selectedAddOns, setSelectedAddOns] = useState([]);
    const [mandatoryVariations, setMandatoryVariations] = useState(item.variation_id?.length > 0  );
    const [quantity, setQuantity] = useState(1);
    const [price, setPrice] = useState(item.sale_price > 0 ? item.sale_price : item.base_price);
    const [displayPrice, setDisplayPrice] = useState(item.sale_price > 0 ? item.sale_price : item.base_price);
    const [oldPrice, setOldPrice] = useState(item.sale_price > 0 ? item.base_price : null);
    const [itemDetailsPlacements, setPlacements] = useState(null);

    const [selectedSlots, setSelectedSlots] = useState([]);
    const [isSlotSelectionRequired, setIsSlotSelectionRequired] = useState(item.field_item_availability_required === 1);


    const notify_error = (message) => toast.error(message, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
    });

    useEffect(() => {
        const loadPlacements = async () => {
            try {
                const placements = await import(`../projects/${appId}/placements/item_details_placements.json`);
                setPlacements(placements.default);
            } catch (error) {
                console.error('Failed to load placements:', error);
            }
        };

        loadPlacements();
    }, [appId]);


    useEffect(() => {
        setDisplayPrice(calculateTotalPrice(price, selectedVariation, selectedAddOns));
    }, [selectedVariation, selectedAddOns, selectedSlots]);



    useEffect(() => {
        if (item.sale_price > 0) {
            setPrice(item.sale_price);
        }
    }, [item.sale_price]);


    const handleSelectedVariation = (selectedVariant) => {
        setMandatoryVariations(false);
        setSelectedVariation(selectedVariant);
        setPrice(selectedVariant.price);
    };


    const handleSelectedAddon = (groupIndex, selectedAddons) => {
        const basePrice = price;
        setSelectedAddOns(prev => {
            // 1) remove old addons for this group
            const others = prev.filter(a => a.groupIndex !== groupIndex);

            // 2) normalize to array
            const addonsArray = Array.isArray(selectedAddons)
                ? selectedAddons
                : [selectedAddons];

            // 3) map into your shape
            const newEntries = addonsArray.map(addon => ({
                groupIndex,
                name: addon.name,
                price: addon.price,
            }));

            const newAddOns = [...others, ...newEntries];

            // 4) recompute price = base + sum(addons)
            const addonsTotal = newAddOns.reduce(
                (sum, a) => sum + Number(a.price),
                0
            );
            setPrice(basePrice + addonsTotal);

            return newAddOns;
        });
    };



    // todo: old legacy block

    const handleVariationSelect = (groupIndex, selectedVariant) => {
        const group = item.field_item_variation_group[groupIndex];

        if (group.field_variant_base_price?.[0] === "1") {
            // ✅ Variations: Single selection, update base price
            setSelectedVariation(selectedVariant);
            setPrice(selectedVariant.price);
        } else {
            // ✅ Add-ons: Handle single or multiple selections
            setSelectedAddOns((prevAddOns) => {
                let newAddOns = [...prevAddOns];

                if (group.field_variant_multiple?.[0] === "1") {
                    // Multiple selections: Replace the entire group's addons
                    newAddOns = newAddOns.filter(addOn => addOn.groupIndex !== groupIndex); // Remove existing addons for this group
                    if (Array.isArray(selectedVariant)) {
                        newAddOns.push(...selectedVariant.map(variant => ({
                            groupIndex,
                            name: variant.name,
                            price: variant.price
                        })));
                    }
                } else {
                    // Single selection: Replace the addon for this group
                    newAddOns = newAddOns.filter(addOn => addOn.groupIndex !== groupIndex); // Remove existing addon for this group
                    if (selectedVariant) {
                        newAddOns.push({
                            groupIndex,
                            name: selectedVariant.name,
                            price: selectedVariant.price
                        });
                    }
                }

                // ✅ Update price based on add-ons
                const totalAddOnsPrice = newAddOns.reduce((acc, addOn) => acc + Number(addOn.price), 0);
                setPrice((selectedVariation?.price || price) + totalAddOnsPrice);

                return newAddOns;
            });
        }
    };


    const handleAavailabiltySelect = (slots, required, basePrice, currentBasePrice) => {
        setSelectedSlots(slots);
        setIsSlotSelectionRequired(required);
        console.log(required);
        if (basePrice) {
            setPrice(currentBasePrice);
        }
    };

    const handleAvailabilityRequired = (required) => {
        console.log(required);
        setIsSlotSelectionRequired(required);
    };

    const handleAddToCart = () => {
        // Ensure item.field_item_variation_group is defined and is an array
        if (item.field_item_variation_group && Array.isArray(item.field_item_variation_group)) {

            for (let i = 0; i < item.field_item_variation_group.length; i++) {
                const group = item.field_item_variation_group[i];
                const isRequired = group.field_variant_required && group.field_variant_required[0] === "1";
                const isBasePrice = group.field_variant_base_price && group.field_variant_base_price[0] === "1";
                const isMultiple = group.field_variant_multiple && group.field_variant_multiple[0] === "1";
                const groupName = group.field_variant_group_name && group.field_variant_group_name[0];

                if (isRequired && isBasePrice && !selectedVariation && group.field_variants?.length > 0) {
                    notify_error(t("item_details_block_variants_error_text") + "  " + (groupName || "this group"));
                    return;
                }

                if (isRequired && !selectedAddOns[i] && !isBasePrice && group.field_variants?.length > 0) {
                    notify_error(t("item_details_block_variants_error_text") + "  " + (groupName || "this group"));
                    return;
                }

                if (isSlotSelectionRequired && selectedSlots.length === 0) {
                    notify_error("Time slot is required");
                    return;
                }
            }
        }


        // for old varations backword compitability



        // for new addons and varations


        if (mandatoryVariations) {
            console.log(mandatoryVariations);
            notify_error(t("item_details_block_variants_error_text"));
            return;
        }

        const byGroup = selectedAddOns.reduce((acc, a) => {
            acc[a.groupIndex] = acc[a.groupIndex] || [];
            acc[a.groupIndex].push(a);
            return acc;
        }, {});



        const invalidFound = (item.addon_group_id || []).some((group, idx) => {
            const chosen = byGroup[idx] || [];
            const count  = chosen.length;

            if (group.is_required && count < group.min_selections) {

                notify_error(group.name +": " + t('item_details_block_addons_group_description_at_least_selections_required_template', { count: group.min_selections }));

                return true; // stop .some() right here
            }
            if (group.is_required && count < group.min_selections) {
                notify_error(
                    group.name + ": " +
                    t("item_details_block_addons_group_description_select_exact_options_template", { count: group.min_selections })
                );
                return true;
            }
            return false;  // ok, continue checking
        });


        if (!invalidFound) {

            let cart_item_price = calculateTotalPrice(price, selectedVariation, selectedAddOns);

            const cartItem = {
                ...item,
                price: cart_item_price,
                selectedVariation: selectedVariation,
                selectedAddOns: selectedAddOns,
                selectedSlots: selectedSlots,
                quantity: quantity,
            };

            console.log(cartItem);

            if (config.global_single_cart_item_enable) {
                cart.clearCart();
            }

            cart.addItem(cartItem);

            if (config.item_details_block_add_to_cart_fast_checkout) {
                navigate('/cart');
            } else {
                navigate('/catalog');
            }
        }
    };


    const handleQuantityChange = (change) => {
        setQuantity(prevQuantity => Math.max(1, prevQuantity + change));
    };

    const handleQuantityIncrease = () => {
        handleQuantityChange(1);
    };

    const handleQuantityDecrease = () => {
        handleQuantityChange(-1);
    };

    const calculateTotalPrice = (price, selectedVariation, selectedAddOns) => {
        // Ensure selectedVariation is not null or undefined before accessing its properties
        const basePrice = selectedVariation && typeof selectedVariation === 'object'
            ? parseFloat(selectedVariation.price)
            : parseFloat(price); // Use the provided price if no variation is selected

        // Ensure selectedAddOns is an array before trying to reduce
        const addOnsPrice = Array.isArray(selectedAddOns)
            ? selectedAddOns.reduce((total, addOn) => {
                return total + parseFloat(addOn.price);
            }, 0)
            : 0;

        // Calculate the total price
        const totalPrice = basePrice + addOnsPrice;

        return totalPrice.toFixed(2); // Return the total price as a string with 2 decimal places
    };


    const data = {
        item_details_block_name: {
            name: item.name,
        },
        item_details_block_specs: {
            specs: item.field_specs,
        },
        item_details_block_short_description: {
            shortDescription: item.short_description,
        },
        item_details_block_variants_groups: {
            variationGroups: item.field_item_variation_group,
            onVariationSelect: handleVariationSelect,

        },
        item_details_block_addons_group: {
            addonGroups: item.addon_group_id,
            onAddonSelect: handleSelectedAddon,

        },
        item_details_block_variations: {
            variation_group_title: item.variation_group_title,
            variation_id: item.variation_id,
            onVariationSelect: handleSelectedVariation,

        },
        item_details_block_price: {
            price: displayPrice,
            oldPrice: oldPrice,
        },
        item_details_block_availability: {
            item: item,
            onAavailabiltyChange: handleAavailabiltySelect,
            availabilityRequired: handleAvailabilityRequired

        },
        item_details_block_images: {
            images: item.item_image,
        },
        item_details_block_add_to_cart: {
            appId: appId,
            onAddToCart: handleAddToCart,
        },
        item_details_block_quantity: {
            quantity: quantity,
            onIncrease: handleQuantityIncrease,
            onDecrease: handleQuantityDecrease,
        },
    };

    if (!itemDetailsPlacements) {
        return <div></div>; // Show a loading state while configuration or data is being loaded
    }

    const renderer = new ComponentRenderer(itemDetailsPlacements, data);

    return (
        <div className="item_details_layout ">
            <div className="item_details_layout_toolbar toolbar">
                <div className="item_details_layout_header_position_header_start toolbar_start">{renderer.renderComponents('item_details_layout_header_position_header_start')}</div>
                <div className="item_details_layout_header_position_header_center toolbar_center">{renderer.renderComponents('item_details_layout_header_position_header_center')}</div>
                <div className="item_details_layout_header_position_header_end toolbar_end">{renderer.renderComponents('item_details_layout_header_position_header_end')}</div>
            </div>
            <div className="item_details_layout_header item_details_layout_header_position_featured">{renderer.renderComponents('item_details_layout_header_position_featured')}</div>
            <div className="item_details_layout_content">
                <div className="item_details_layout_content_row_top flex">
                    <div className="item_details_layout_content_row_top_position_start">{renderer.renderComponents('item_details_layout_content_row_top_position_start')}</div>
                    <div className="item_details_layout_content_row_top_position_end">{renderer.renderComponents('item_details_layout_content_row_top_position_end')}</div>
                </div>
                <div className="item_details_layout_content_row_middle flex">
                    <div className="item_details_layout_content_row_middle_position_start">{renderer.renderComponents('item_details_layout_content_row_middle_position_start')}</div>
                    <div className="item_details_layout_content_row_middle_position_end">{renderer.renderComponents('item_details_layout_content_row_middle_position_end')}</div>
                </div>
                <div className="item_details_layout_content_row_bottom flex">
                    <div className="item_details_layout_content_row_bottom_position_start">{renderer.renderComponents('item_details_layout_content_row_bottom_position_start')}</div>
                    <div className="item_details_layout_content_row_bottom_position_end">{renderer.renderComponents('item_details_layout_content_row_bottom_position_end')}</div>
                </div>
            </div>
            {config.item_details_layout_footer_enable && (
            <div className="item_details_layout_footer_holder">
                <div className="item_details_layout_footer">
                    <div className="item_details_layout_footer_positions">
                        <div className="item_details_layout_footer_position_footer_start">{renderer.renderComponents('item_details_layout_footer_position_footer_start')}</div>
                        <div className="item_details_layout_footer_position_footer_center">{renderer.renderComponents('item_details_layout_footer_position_footer_center')}</div>
                        <div className="item_details_layout_footer_position_footer_end">{renderer.renderComponents('item_details_layout_footer_position_footer_end')}</div>
                    </div>
                </div>
            </div>
            )}
            <ToastContainer />
        </div>
    );
};

export default ItemDetails;
